import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useAuth } from '@/lib/auth'
import { calculateDuration, getCurrentISOString } from '@/lib/dates'
import { getDeviceLocation } from '@/lib/device-location'
import { captureException } from '@/lib/errors'
import { log } from '@/lib/logger'
import { useMutation } from '@/lib/mutations'
import { offlineExecutor } from '@/lib/offline'
import { newRecordId, useStore } from '@/lib/pocketbase'

interface ActiveEntryToClose {
    id: string
    clock_in: string
}

interface ClockInData {
    jobId: string
    locationId?: string
    notes?: string
    activeEntryToClose?: ActiveEntryToClose
    activeBreakToClose?: ActiveEntryToClose
}

export function useClockIn() {
    const { user } = useAuth()
    const { org: currentOrg } = useCurrentOrg({ throwIfNotFound: false })
    const [timeEntriesCollection] = useStore('time_entries')

    const mutation = useMutation({
        networkMode: 'always',
        mutationFn: async (data: ClockInData) => {
            if (!user?.id) {
                throw new Error('User not found')
            }
            if (!currentOrg) {
                throw new Error('Organization not found')
            }

            const clockInTime = getCurrentISOString()

            const entryId = newRecordId()
            const tx = offlineExecutor.createOfflineTransaction({
                mutationFnName: 'syncTimeEntries',
            })
            tx.mutate(() => {
                if (data.activeBreakToClose) {
                    const breakDuration = calculateDuration({
                        clock_in: data.activeBreakToClose.clock_in,
                        clock_out: clockInTime,
                    })
                    log.info('Auto-closing active break before new clock-in', {
                        entryId: data.activeBreakToClose.id,
                        duration: breakDuration,
                    })
                    timeEntriesCollection.update(data.activeBreakToClose.id, draft => {
                        draft.clock_out = clockInTime
                        draft.status = 'pending'
                        draft.duration = breakDuration
                    })
                }

                if (data.activeEntryToClose) {
                    const duration = calculateDuration({
                        clock_in: data.activeEntryToClose.clock_in,
                        clock_out: clockInTime,
                    })
                    log.info('Auto-closing active entry before new clock-in', {
                        entryId: data.activeEntryToClose.id,
                        duration,
                    })
                    timeEntriesCollection.update(data.activeEntryToClose.id, draft => {
                        draft.clock_out = clockInTime
                        draft.status = 'pending'
                        draft.duration = duration
                    })
                }

                timeEntriesCollection.insert({
                    id: entryId,
                    user: user.id,
                    org: currentOrg.id,
                    job: data.jobId,
                    location: data.locationId || '',
                    clock_in: clockInTime,
                    clock_out: '',
                    status: 'active',
                    entry_type: 'timer',
                    notes: data.notes || '',
                    duration: 0,
                    approved_by: '',
                    approved_at: '',
                    modified_by: '',
                    rejection_reason: '',
                    clock_in_location: null,
                    clock_out_location: null,
                })
            })

            getDeviceLocation()
                .then(location => {
                    if (!location) return
                    const locationTx = offlineExecutor.createOfflineTransaction({
                        mutationFnName: 'syncTimeEntries',
                    })
                    locationTx.mutate(() => {
                        timeEntriesCollection.update(entryId, draft => {
                            draft.clock_in_location = location
                        })
                    })
                    log.debug('Clock-in location captured', location)
                })
                .catch(() => {})
        },
        onError: error => {
            captureException('Clock in failed', error)
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}
